import { CostUnit, RehabAction, RehabResults } from '@Types';
import { selectUserContext } from '@Selectors';
import {
	Drawer,
	closeDrawer,
	useUnits,
	useSettings,
	getCurrency,
	HTMLTextAreaElementExtended,
} from '@innovyze/stylovyze';
import {
	Backdrop,
	CircularProgress,
	Grid,
	TextField,
	Typography,
} from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useStyles } from './RehabResultDrawer.styles';
import {
	selectIsCostLoading,
	selectRehabActions,
	selectRehabCost,
} from '@Selectors/Rehab.selectors';
import { editAction, getRehabCost } from '@Actions/Rehab.actions';
import { useGlobalization } from '@Translations';
import { AssetTypeId } from '@Types/assetSchema.types';
import { formatCostBreakdown } from './utils/formatCostBreakdown';
import { RehabActionField } from './components/RehabActionField';
import { CalculatedCostSummary } from './components/CalculatedCostSummary';
import { CostOverrideField } from './components/CostOverrideField';

interface Props {
	treeId: string;
	assetType: AssetTypeId;
	viewerAccess: boolean;
	rehabResult: RehabResults | undefined | null;
}

const emptyAction = (treeId: string, notes: string): RehabAction => ({
	_id: '',
	actionId: '',
	treeId,
	unitCost: 0,
	unit: CostUnit.each,
	settings: { enabled: true, defects: [] },
	notes,
});

export const RehabResultDrawer = ({
	treeId,
	assetType,
	viewerAccess,
	rehabResult,
}: Props) => {
	const { t } = useGlobalization();
	const classes = useStyles();
	const dispatch = useDispatch();

	const { system } = useUnits();
	const { companySettings } = useSettings();
	const userContext = selectUserContext();

	const formatCurrency = (n: number): string =>
		getCurrency(n, companySettings) ?? '';

	const getRehabAction = (
		result: RehabResults | null | undefined,
	): RehabAction =>
		result?.rehabAction ?? emptyAction(treeId, result?.notes ?? '');

	const [rehabAction, setRehabAction] = useState<RehabAction>(
		getRehabAction(rehabResult),
	);
	const [calcTotal, setCalcTotal] = useState<number>(
		rehabResult?.totalCost && rehabResult.totalCost >= 0
			? rehabResult.totalCost
			: 0,
	);
	const [calcCostString, setCalcCostString] = useState<string>(
		rehabResult?.costCalculation ?? '',
	);
	const [overrideAmount, setOverrideAmount] = useState<number>(
		rehabResult?.totalCost && rehabResult.totalCost >= 0
			? rehabResult.totalCost
			: 0,
	);
	const [notes, setNotes] = useState<string>(rehabResult?.notes ?? '');
	const [isOverrideCost, setIsOverrideCost] = useState<boolean>(false);
	const [locked, setLocked] = useState<string>(rehabResult?.locked ?? '0');
	const [isDirty, setIsDirty] = useState<boolean>(false);

	const rehabActions = selectRehabActions();
	const rehabCost = selectRehabCost();
	const isCostLoading = selectIsCostLoading();

	const isLocked = locked === '1';
	const overrideDisabled = viewerAccess || isLocked;

	// The calculated cost summary always reflects the latest backend calculation.
	useEffect(() => {
		if (rehabCost) {
			setCalcTotal(rehabCost.TOTAL_COST ?? 0);
			const calcString =
				system === 'Metric'
					? rehabCost.COST_CALCULATION_METER
					: rehabCost.COST_CALCULATION_FT;
			setCalcCostString(calcString ?? '');
		}
	}, [rehabCost, system]);

	const fetchCalculatedCost = (action: RehabAction) => {
		dispatch(
			getRehabCost({
				treeId,
				actionId: action._id,
				assetId: rehabResult?.assetId ?? '',
				assetType,
			}),
		);
	};

	const handleActionChange = (actionId: string) => {
		setIsDirty(true);
		const found = rehabActions.find(a => a.actionId === actionId);
		if (found) {
			setRehabAction(found);
			fetchCalculatedCost(found);
		}
	};

	const handleLockToggle = () => {
		setIsDirty(true);
		const next = isLocked ? '0' : '1';
		setLocked(next);
		// Locking disables overriding, so turn override off when locking.
		if (next === '1') setIsOverrideCost(false);
	};

	const handleOverrideToggle = (checked: boolean) => {
		setIsDirty(true);
		setIsOverrideCost(checked);
		// Seed an empty override with the calculated total as a starting point.
		if (checked && overrideAmount === 0) {
			setOverrideAmount(calcTotal);
		}
	};

	const handleAmountChange = (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElementExtended>,
	) => {
		setIsDirty(true);
		const amount = Number(event.target.value);
		setOverrideAmount(Number.isNaN(amount) ? 0 : amount);
	};

	const handleNotesChange = (event: ChangeEvent<HTMLInputElement>) => {
		setIsDirty(true);
		setNotes(event.target.value);
	};

	const { value, breakdown } = formatCostBreakdown({
		rehabAction,
		costCalculation: calcCostString,
		totalCost: calcTotal,
		system,
		t,
		formatCurrency,
	});

	const handleSave = () => {
		const modifiedRehabResult: RehabResults = {
			treeId,
			assetId: rehabResult?.assetId ?? '',
			rehabAction: { ...rehabAction, notes },
			costCalculation: isOverrideCost
				? t('Cost Override by: {{username}}', {
						username: userContext.name,
					})
				: calcCostString,
			totalCost: isOverrideCost ? overrideAmount : -1,
			locked,
			draftRehabAction: rehabResult?.draftRehabAction,
			actionNodeId: rehabResult?.actionNodeId,
		};
		dispatch(editAction(modifiedRehabResult));
	};

	return (
		<Drawer
			action={{
				label: t('Save Changes'),
				onClick: () => {
					handleSave();
					dispatch(closeDrawer());
				},
				disabled: !isDirty,
			}}
			drawerId="editRehabResultDrawer"
			closeOnClickAway={false}
			onCancel={() => dispatch(closeDrawer())}
			onOpen={() => {
				setIsDirty(false);
				const action = getRehabAction(rehabResult);
				const wasOverride =
					!!rehabResult?.costCalculation?.includes('Override');
				const persistedTotal =
					rehabResult?.totalCost && rehabResult.totalCost >= 0
						? rehabResult.totalCost
						: 0;
				setRehabAction(action);
				setIsOverrideCost(wasOverride);
				setOverrideAmount(persistedTotal);
				setCalcTotal(persistedTotal);
				setCalcCostString(
					wasOverride ? '' : (rehabResult?.costCalculation ?? ''),
				);
				setLocked(rehabResult?.locked ?? '0');
				setNotes(
					rehabResult?.notes ?? rehabResult?.rehabAction?.notes ?? '',
				);
				// Always refresh the calculated cost so the summary stays accurate.
				fetchCalculatedCost(action);
			}}
			title={t('Override action or cost')}>
			<>
				<Backdrop open={isCostLoading} className={classes.drawerLoader}>
					<CircularProgress color="inherit" />
				</Backdrop>
				<form
					className={classes.formRoot}
					noValidate
					autoComplete="off">
					<Typography
						style={{
							fontSize: '14px',
							color: '#768D95',
							marginBottom: '16px',
						}}>
						{t('Manually adjust the action or cost for this asset')}
					</Typography>
					<Grid container spacing={2}>
						<RehabActionField
							rehabActions={rehabActions}
							selectedActionId={rehabAction?.actionId ?? ''}
							locked={isLocked}
							disabled={viewerAccess}
							onActionChange={handleActionChange}
							onLockToggle={handleLockToggle}
						/>
						<CalculatedCostSummary
							value={value}
							breakdown={breakdown}
							loading={isCostLoading}
						/>
						<CostOverrideField
							isOverride={isOverrideCost}
							overrideAmount={overrideAmount}
							disabled={overrideDisabled}
							onToggle={handleOverrideToggle}
							onAmountChange={handleAmountChange}
						/>
						<Grid xs={12} item>
							<TextField
								fullWidth
								name="Notes"
								multiline
								value={notes}
								onChange={handleNotesChange}
								label={t('Notes (optional)')}
								variant="outlined"
								disabled={viewerAccess}
							/>
						</Grid>
					</Grid>
				</form>
			</>
		</Drawer>
	);
};
